import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import api, { post } from '../api/apiService';
import { validateIndianPhone, errorStyle } from '../utils/loginUtils';
import image from '../assets/icons/nayonika-group.png';
import edit from '../assets/icons/phoneno-edit.png';
import { apiEndPoints } from '../api/apiEndPoints';

const DeleteUserAccount: React.FC = () => {
  // State management
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('phone');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showOtpInput, setShowOtpInput] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(['', '', '', '']);
  const [sessionId, setSessionId] = useState<string>('');
  const [otpSentTo, setOtpSentTo] = useState<string>('');
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [resendTimer, setResendTimer] = useState<number>(30);
  const [notFoundError, setNotFoundError] = useState<boolean>(false);
  const [accountDeleted, setAccountDeleted] = useState<boolean>(false);
  const [userId, setUserId] = useState<string | null>('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState<boolean>(false);
  const navigate = useNavigate();

  // Handle login method change (Phone number or Email Address)
  const handleLoginMethodChange = (method: 'phone' | 'email') => {
    setLoginMethod(method);
    setShowOtpInput(false);
    setErrorMessage('');
    setNotFoundError(false);
    setPhoneNumber('');
    setEmail('');
    setOtp(['', '', '', '']);
  };

  // Handle OTP request
  const handleRequestOtp = async () => {
    if (loginMethod === 'phone' && phoneNumber) {
      setOtpSentTo(`+91 ${phoneNumber}`);

      if (!validateIndianPhone(phoneNumber)) {
        setErrorMessage('Please enter a valid mobile number');
        setNotFoundError(false);
        return;
      }

      setErrorMessage('');
      setIsSubmitting(true);
      setNotFoundError(false);
      localStorage.removeItem('jwtToken'); // Clear any existing token
      try {
        // const resData: any = await post_RequestOtp(phoneNumber);
        const resData: any = await post_RequestOtp(phoneNumber);
        if (!resData?.sessionId) {
          setNotFoundError(true);
          return;
        }
        setSessionId(resData.sessionId);
        setUserId(resData.userId || '');
        setShowOtpInput(true);
        setResendTimer(30);
        setOtp(['', '', '', '']);
      } catch (error: any) {
        if (error instanceof Error && error.message.toLowerCase().includes('not found')) {
          setNotFoundError(true);
        } else {
          setErrorMessage(error.message || 'Login failed. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    } else if (loginMethod === 'email' && email) {
      setOtpSentTo(email);
      setShowOtpInput(true);
      setErrorMessage('');
      setNotFoundError(false);
      setOtp(['', '', '', '']);
    }
  };

  const post_RequestOtp = async (phoneNumber: string) => {
    const data = {
      applicationCode: 'WEB_PORTAL',
      credential: phoneNumber,
    };
    try {
      const url = apiEndPoints.requestOTPEndPoint;
      const response = await post(url, data);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to request OTP');
    }
  };

  useEffect(() => {
    if (showOtpInput && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [showOtpInput]);

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isNaN(Number(value))) return;

    if (errorMessage) {
      setErrorMessage('');
    }

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value && index < otp.length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleClick = (index: number) => {
    inputRefs.current[index]?.setSelectionRange(1, 1);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP verification
  const handleVerifyOtp = async () => {
    const combinedOtp = otp.join("");

    if (combinedOtp.length !== otp.length) {
      setErrorMessage('Please enter complete OTP');
      return;
    }

    setIsVerifyingOtp(true);

    try {
      const url = apiEndPoints.verifyOTPEndPoint;
      const data = {
        otp: combinedOtp,
        sessionId: sessionId
      };
      const resData: any = await post(url, data);
      if (!resData || !resData.token) {
        setErrorMessage('Invalid OTP. Please try again.');
        return;
      }
      setErrorMessage('');
      const jwtToken = resData.token;
      localStorage.setItem('jwtToken', jwtToken);
      const response = await deleteUserAccount(`${apiEndPoints.deleteUserAccountURL}/${userId}`, jwtToken);
      console.log('Account deletion response:', response);
      setIsVerifyingOtp(false);
      setAccountDeleted(true);
    } catch (error) {
      setErrorMessage('Invalid OTP. Please try again.');
      setIsVerifyingOtp(false);
      console.error('OTP verification error:', error);
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (showOtpInput && resendTimer > 0) {
      intervalId = setInterval(() => {
        setResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [showOtpInput, resendTimer]);

  // Handle Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    try {
      const data = {
        sessionId: sessionId
      };
      const url = apiEndPoints.reSendOTPEndPoint;
      const resData: any = await post(url, data);
      if (!resData?.sessionId) {
        alert('Error resending OTP');
        return;
      }
      setSessionId(resData.sessionId);
      setShowOtpInput(true);
      setResendTimer(30);
      setOtp(['', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const deleteUserAccount = async (url: string,token:string) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const response = await api.delete(url, { headers });
      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        alert('Failed to delete data: ' + err.message);
        throw new Error(`Failed to delete data: ${err.message}`);
      } else {
        alert('An unknown error occurred during DELETE request.');
        throw new Error('An unknown error occurred during DELETE request');
      }
    }
  };

  // Success message after account deletion
  if (accountDeleted) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-white px-4">
        <div className="bg-green-500 rounded-full p-3 mb-6">
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <p className="text-xl font-medium text-gray-800 mb-8">
          Your account has been deleted successfully.
        </p>
        {/* <button
          className="bg-indigo-700 hover:bg-indigo-800 text-white px-8 py-2 rounded-full text-base font-medium"
          onClick={() => navigate('/')}
        >
          Ok
        </button> */}
      </div>
    );
  }

  return (
    <div className="flex flex-row bg-white items-center h-screen">
      {/* Logo */}
      <div className="flex items-center text-center justify-center w-[50%] h-full bg-[#F9F7ED] bg-center" style={{ backgroundSize: 'cover', backgroundImage: `url(${image})` }}>
        <div className="flex flex-row items-center justify-center">
          <img
            src="/nayonika_new.png"
            alt="Logo"
            className="w-[244px] h-[192px]"
          />
        </div>
      </div>
      {/* Login Container */}
      <div className="flex flex-col items-start justify-center w-[50%] h-full pl-12">
        <h1 className="text-3xl font-bold mb-2">Delete My Account</h1>
        <p className="text-sm font-normal text-gray-600 mb-4">Please enter your registered Phone Number</p>
        {/* Login Method Tabs */}
        <div className="flex items-center justify-center mb-6">
          <button
            className={`mr-6 pb-2 text-[#40559C] ${loginMethod === 'phone'
              ? 'border-b-2 border-[#40559C] font-medium text-[#40559C]'
              : 'text-gray-500'}`}
            onClick={() => handleLoginMethodChange('phone')}
          >
            Phone number
          </button>
        </div>
        {/* Form Content */}
        {!showOtpInput ? (
          <div className="mb-8">
            {loginMethod === 'phone' ? (
              <div className="relative">
                <input
                  type="tel"
                  maxLength={10}
                  className="w-full border rounded-full py-3 px-4 pl-12 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  placeholder="Enter mobile number"
                  value={phoneNumber}
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/[^0-9]/g, '');
                    setPhoneNumber(newValue);
                    setErrorMessage('');
                    setNotFoundError(false);
                  }}
                  onFocus={() => {
                    //setErrorMessage('');
                    //setNotFoundError(false);
                  }}
                />
                <span className="absolute left-4 top-3 text-gray-600">+91</span>
              </div>
            ) : (
              <input
                type="email"
                className="w-full border rounded-full py-3 px-4 focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="Enter email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
            {errorMessage && <div style={errorStyle}>{errorMessage}</div>}
            <button
              className="w-full bg-black text-white rounded-full py-3 mt-6 hover:bg-gray-800 transition-colors"
              style={{ cursor: phoneNumber.length < 10 ? 'not-allowed' : 'pointer', backgroundColor: phoneNumber.length === 10 ? '#40559C' : '#cccccc', }}
              disabled={phoneNumber.length < 10}
              onClick={handleRequestOtp}
            >
              {isSubmitting ? (

                'Request OTP in progress...'

              ) : (
                'Request OTP'
              )}
            </button>
            {!notFoundError ? (
              <div className="mt-10  w-full max-w-[420px]">
                <div className="flex items-start gap-3 p-4 bg-[#FCF9EA]  border-yellow-400 rounded-md text-sm text-gray-900">
                  <div className="mt-2 text-yellow-500">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.518 11.591c.75 1.335-.213 2.99-1.742 2.99H3.48c-1.53 0-2.492-1.655-1.743-2.99L8.257 3.1zm1.243 10.401a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5zm.75-6.75a.75.75 0 00-1.5 0v4.5a.75.75 0 001.5 0v-4.5z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p>
                    Please note that your account and your data will be permanently removed.
                    This action cannot be reversed.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-10 w-full max-w-[420px]">
                <div className="flex items-start gap-3 p-4 bg-red-50  border-red-500 rounded-md text-sm text-gray-900">
                  <div className="mt-2 text-red-600">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-8.75-3.75a.75.75 0 011.5 0v4.5a.75.75 0 01-1.5 0v-4.5zM10 13a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p>
                    We couldn't find this number in our records.
                    Please verify and retry.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Step 2: OTP Verification
          <div className="mb-8">
            <p className="text-center mb-4 text-gray-600">
              Enter OTP sent to
              <span className="ml-1 text-[#40559C] font">{otpSentTo}
                <button className="ml-2"
                  onClick={() => { setShowOtpInput(false); setOtp(['', '', '', '']); }}>
                  <img src={`${edit}`}
                    alt="edit"
                    className="w-[16px] h-[16px]"
                  />
                </button>
              </span>
            </p>
            <div className="flex justify-center space-x-4 mb-6">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  autoComplete="off"
                  className="w-12 h-12 text-center border-[1.6px] rounded-[16px] focus:outline-none focus:ring-1 focus:ring-[#40559C]-700 text-lg"
                  value={digit}
                  ref={(input) => { inputRefs.current[index] = input; }}
                  onChange={(e) => handleChange(index, e)}
                  onClick={() => handleClick(index)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                />
              ))}
            </div>
            {errorMessage && <div style={errorStyle}>{errorMessage}</div>}
            <button
              className="w-full bg-[#40559C] text-white rounded-full py-3 mt-3 transition-colors"
              onClick={handleVerifyOtp}
              disabled={isVerifyingOtp}
            >
              {isVerifyingOtp ? (
                'Account delete in progress...'
              ) : (
                'Verify & Continue'
              )}
            </button>
            <div className="mt-4">
              <span className="text-gray-600">Didn't receive? </span>
              <button
                className={`${resendTimer > 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-[#40559C] hover:text-[#2a3a7c]'
                  } font-medium`}
                onClick={handleResendOtp}
                disabled={resendTimer > 0}
              >
                Resend OTP
              </button>
              {resendTimer > 0 && <span className="ml-1 text-gray-600">in {resendTimer} seconds</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteUserAccount;